import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, getStoredToken, setStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Per-tab authentication.
 *
 * The token lives in sessionStorage (isolated per browser tab) instead of a
 * cross-tab httpOnly cookie. This lets, for example, the super-admin be
 * logged in in one tab while a regular user is logged in in another tab of
 * the same browser without either session leaking into the other.
 *
 * The server verifies the JWT (and the current role) on EVERY request via the
 * `get_current_user` / `require_admin` dependencies, so client-side state is
 * only a UX hint — it never grants access on its own.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, object=user
  const [loading, setLoading] = useState(true);
  const pendingRef = useRef(false);

  const revalidate = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    try {
      if (!getStoredToken()) {
        setUser(false);
        return;
      }
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      // 401 handler in api.js already cleared the token.
      setUser(false);
    } finally {
      pendingRef.current = false;
    }
  };

  useEffect(() => {
    (async () => {
      await revalidate();
      setLoading(false);
    })();
    // Refresh identity when the tab regains focus (server may have changed
    // is_active / has_paid / role in the meantime).
    const onFocus = () => { if (!pendingRef.current) revalidate(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) setStoredToken(data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, originUrl) => {
    // Feb 2026: free trial 7gg senza carta. Il backend crea l'account subito
    // e imposta il cookie JWT. Nessuna Stripe Checkout qui.
    const { data } = await api.post("/auth/register", {
      email, password, name,
      origin_url: originUrl || window.location.origin,
    });
    if (data?.token) setStoredToken(data.token);
    setUser(data);
    return data; // { token, user_public..., trial_granted }
  };

  const completeSignup = async (sessionId) => {
    const { data } = await api.post("/auth/complete-signup", { session_id: sessionId });
    if (data?.token) setStoredToken(data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    setStoredToken(null);
    setUser(false);
  };

  const refresh = (nextUser) => {
    if (nextUser) {
      setUser(nextUser);
      return Promise.resolve(nextUser);
    }
    return api.get("/auth/me").then((res) => {
      setUser(res.data);
      return res.data;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, completeSignup, logout, refresh, revalidate }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
